'use client';

import React, { useState } from 'react';
import { cva } from 'class-variance-authority';

import { ChevronRightFilled } from '@zealy/icons';

import { RewardCard } from '#components/RewardCard';
import { cn, formatZapsValue } from '#utils/utils';

import type { SprintReward } from '../SprintCard.types';

const rewardContainer = cva(
  ['group/reward-stack', 'transition-all ease-in-out duration-500 translate-x-0'],
  {
    variants: {
      position: {
        0: [
          'z-30',
          'group-data-[expendable=true]/rewards:group-data-[expanded=false]/rewards:group-hover/rewards:translate-x-[134px]',
          'group-data-[expanded=false]/rewards:translate-x-[142px]',
        ],
        1: [
          'z-20',
          'group-data-[expendable=true]/rewards:group-data-[expanded=false]/rewards:group-hover/rewards:translate-x-[67px]',
          'group-data-[expanded=false]/rewards:translate-x-[72px]',
        ],
        2: ['z-10'],
      },
      scale: {
        0: ['group-data-[expanded=false]/rewards:scale-100'],
        1: ['group-data-[expanded=false]/rewards:scale-90'],
        2: ['group-data-[expanded=false]/rewards:scale-75'],
      },
    },
  },
);

const stage = cn([
  'rounded-quest-card-questcard flex items-center justify-end transition-all duration-400',
  'group-data-[expanded=false]/rewards:w-reward-card-img-sm group-data-[expanded=false]/rewards:h-reward-card-img-sm',
  'group-data-[expanded=true]/rewards:pr-quest-card-md-left group-data-[expanded=true]/rewards:backdrop-blur-md group-data-[expanded=true]/rewards:absolute group-data-[expanded=true]/rewards:inset-0',
]);

type RewardContainerVariants = {
  position?: 0 | 1 | 2;
  scale?: 0 | 1 | 2;
};

const rewardOrder = {
  usdc: 1,
  zaps: 2,
} as const;

const sortRewards = (a: SprintReward, b: SprintReward) => {
  return rewardOrder[a.type] - rewardOrder[b.type];
};

export const SprintRewardStack = ({ rewards }: { rewards?: SprintReward[] }) => {
  const [expanded, setExpanded] = useState(false);

  if (!rewards || rewards.length === 0) return null;

  // Filter out rewards with 0 or undefined values (empty pools)
  const validRewards = rewards.filter(reward => reward.value && reward.value > 0);

  if (validRewards.length === 0) return null;

  const rewardCards = validRewards.sort(sortRewards).slice(0, 3);
  const rewardsCount = rewardCards.length;

  const toggleExpanded = () => setExpanded(prev => !prev);

  const isRewardsAreaExpandable = rewardsCount > 1;

  const onRewardsClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isRewardsAreaExpandable) {
      return;
    }

    toggleExpanded();
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div
      data-testid="rewards"
      onClick={onRewardsClick}
      className={cn(
        'group/rewards z-10 flex items-center content-center py-quest-card-md-left pr-quest-card-md-left right-0 min-w-[80px]',
        expanded && 'absolute inset-0',
      )}
      data-expanded={expanded}
      data-expendable={isRewardsAreaExpandable}
    >
      <div className={stage}>
        <ChevronRightFilled className="opacity-0 transition-transform duration-1000 translate-x-400 group-data-[expanded=true]/rewards:opacity-100 group-data-[expanded=true]/rewards:translate-x-0 absolute left-400" />
        <div className="flex gap-200 items-center group-data-[expanded=false]/rewards:-mr-100 group-data-[expanded=false]/rewards:[&>*:not(:first-child)>div>div>svg]:hidden">
          {rewardCards.map((reward, index) => {
            const diff = 3 - rewardsCount;
            const position = (index + diff) as RewardContainerVariants['position'];
            const scale = index as RewardContainerVariants['scale'];

            const rewardClassName = rewardContainer({
              position,
              scale,
            });

            return (
              <div key={`${reward.type}-${index}`} className="relative">
                <RewardCard
                  data-testid="reward-card"
                  type={reward.type}
                  value={
                    reward.type === 'zaps' ? formatZapsValue(reward.value) : Number(reward.value)
                  }
                  quest={{ id: '', categoryId: '' }}
                  bgColor="var(--color-bg-secondary)"
                  overlay={index > 0 && !expanded}
                  className={rewardClassName}
                  size="sm"
                />
                {index > 0 && !expanded && (
                  <div className="absolute inset-0 bg-black/20 rounded-reward-card-container pointer-events-none" />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
